import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { getTeachers, generateQaReport } from "../../../Api/apiUtils";
import { toast } from "react-toastify";
import { MixpanelEvents, trackEvent } from "../../../utils/mixpanelTracking";
import {
  Page,
  PageHeader,
  Card,
  Button,
  Field,
  TextInput,
  SearchInput,
  Table,
  EmptyState,
} from "../../../ui";
import "./qaPolicy.scss";

interface Teacher {
  teacherId: string;
  teacherName: string;
  mobileNumber?: string;
  emailId?: string;
}

interface QaReport {
  teacherName: string;
  qaScore: number;
  auditDate: string;
}

const QaPolicy: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
  const [qaReportDetail, setQaReportDetail] = useState<QaReport[]>([]);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [teachersList, setTeachersList] = useState<Teacher[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const navigate = useNavigate();

  const handleModalClose = () => {
    setSelectedTeacherId(null);
    setModalVisible(false);
  };

  const handleAddTeacher = () => {
    const selectedTeacher = teachersList.find((t) => t.teacherId === selectedTeacherId);
    if (selectedTeacher) setSelectedTeacher(selectedTeacher.teacherName);
    setModalVisible(false);
  };

  const onFinish = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select a date range.");
      return;
    }
  
    try {
      setIsLoading(true);
      const teacherId = selectedTeacherId || undefined;
      const data = await generateQaReport(fromDate, toDate, teacherId);
      
      setQaReportDetail(data);
      trackEvent(MixpanelEvents.QA_POLICY_REPORT_GENERATED, {
        fromDate,
        toDate,
        teacherIdFilter: teacherId != null ? String(teacherId) : undefined,
        rowCount: Array.isArray(data) ? data.length : 0,
      });
      
      if (!data || data.length === 0) {
        toast.info("No data found for the selected criteria");
      }
    } catch (error) {
      console.error("Failed to fetch QA reports:", error);
      toast.error("Failed to generate report. Please try again.");
      trackEvent(MixpanelEvents.QA_POLICY_REPORT_GENERATE_FAILED, {
        fromDate,
        toDate,
        teacherIdFilter: selectedTeacherId != null ? String(selectedTeacherId) : undefined,
      });
    } finally {
      setIsLoading(false);
    }
};
  const handleSearch = async () => {
    const searchPayload = { name: searchQuery };
    await fetchTeachers(searchPayload);
    setModalVisible(true);
  };

  const fetchTeachers = async (searchPayload: { name?: string, emailId?: string, mobileNumber?: string } = {}) => {
    try {
      const fetchedTeachers = await getTeachers(0, 200, searchPayload);
      if (Array.isArray(fetchedTeachers)) {
        setTeachers(fetchedTeachers);
        const credentialIds = fetchedTeachers.map((teacher) => teacher.credentialId);
        localStorage.setItem("TeachercredentialIds", JSON.stringify(credentialIds));
      } else {
        console.error("Fetched data is not an array:", fetchedTeachers);
      }
    } catch (error) {
      console.error("There was an error fetching the teacher data!", error);
    }
  };

  return (
    <Page>
      <PageHeader
        eyebrow="Teacher Operations"
        title="QA Policy"
        subtitle="Generate QA scores for a date range, for all teachers or one teacher."
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate("/pages/get-qa")}>
              Get Audit List
            </Button>
            <Button variant="secondary" onClick={() => navigate("/pages/add-qa")}>
              Add Audit List
            </Button>
          </>
        }
      />

      <Card title="Generate report">
        <form className="qa-report-form" onSubmit={(e) => { e.preventDefault(); onFinish(); }}>
          <div className="qa-inline">
            <Field label="From date" id="fromDate">
              {(ctl) => (
                <TextInput {...ctl} type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              )}
            </Field>
            <Field label="To date" id="toDate">
              {(ctl) => (
                <TextInput {...ctl} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              )}
            </Field>
          </div>

          <div className="qa-inline">
            <Field label="Search teacher" id="searchTeacher">
              {(ctl) => (
                <SearchInput
                  {...ctl}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Enter Teacher Name"
                />
              )}
            </Field>
            <Button variant="secondary" disabled={!searchQuery.trim()} onClick={handleSearch}>
              Search
            </Button>
          </div>

          <Field label="Teacher name" id="teacherName" hint="Pick a teacher with Search, or leave empty for all teachers.">
            {(ctl) => (
              <TextInput {...ctl} type="text" value={selectedTeacher} disabled placeholder="Enter Teacher Name" />
            )}
          </Field>

          <div className="qa-report-form__actions">
            <Button type="submit" variant="primary" onClick={handleAddTeacher} loading={isLoading}>
              Generate Report
            </Button>
          </div>
        </form>
      </Card>

      <Modal isOpen={modalVisible} toggle={handleModalClose}>
        <ModalHeader toggle={handleModalClose}>Select Teacher</ModalHeader>
        <ModalBody className="qa-modal-body">
          {teachers.length === 0 ? (
            <EmptyState title="No teachers found" description="Try a different spelling of the teacher's name." />
          ) : (
            <ul className="qa-picker">
              {teachers.map((teacher) => (
                <li key={teacher.teacherId} className="qa-picker__item">
                  <input
                    id={`qa-teacher-${teacher.teacherId}`}
                    className="levelChk"
                    type="radio"
                    name="selectedTeacher"
                    value={teacher.teacherName}
                    checked={selectedTeacher === teacher.teacherName}
                    onChange={() => {
                      setSelectedTeacher(teacher.teacherName);
                      setSelectedTeacherId(teacher.teacherId);
                    }}
                  />
                  <label htmlFor={`qa-teacher-${teacher.teacherId}`}>{teacher.teacherName}</label>
                </li>
              ))}
            </ul>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={handleModalClose}>Cancel</Button>
          <Button variant="primary" onClick={handleAddTeacher}>Select and Add Teacher</Button>
        </ModalFooter>
      </Modal>

      {/* Display the QA Report */}
      {qaReportDetail.length > 0 && (
        <Card title="QA report" flush>
          <Table caption="QA scores for the selected date range">
            <thead>
              <tr>
                <th>Teacher Name</th>
                <th className="ds-num">QA Score</th>
                <th>Audit Date</th>
              </tr>
            </thead>
            <tbody>
              {qaReportDetail.map((report, index) => (
                <tr key={index}>
                  <td>{report.teacherName}</td>
                  <td className="ds-num">{report.qaScore}</td>
                  <td>{report.auditDate}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </Page>
  );
};

export default QaPolicy;